import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './DataDisplay.css';
import API_CONFIG from '../../config/api';

const DataDisplay = () => {
    const [data, setData] = useState([]);
    const [tables, setTables] = useState([]);
    const [selectedTable, setSelectedTable] = useState('users');
    const [columns, setColumns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Fetch available tables on component mount
    useEffect(() => {
        fetchTables();
    }, []);

    // Fetch data when selected table changes
    useEffect(() => {
        if (selectedTable) {
            fetchTableData();
            fetchTableColumns();
        }
    }, [selectedTable]);

    const fetchTables = async () => {
        try {
            const response = await axios.get(`${API_CONFIG.BASE_URL}/api/data/getTables`, {
                withCredentials: true
            });
            if (response.data.success) {
                setTables(response.data.tables);
            }
        } catch (err) {
            console.error('Error fetching tables:', err);
        }
    };

    const fetchTableColumns = async () => {
        try {
            const response = await axios.get(`${API_CONFIG.BASE_URL}/api/data/getTableColumns`, {
                params: { table: selectedTable },
                withCredentials: true
            });
            if (response.data.success) {
                setColumns(response.data.columns);
            }
        } catch (err) {
            console.error('Error fetching columns:', err);
        }
    };

    const fetchTableData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_CONFIG.BASE_URL}/api/data/getAllData`, {
                params: { table: selectedTable },
                withCredentials: true
            });

            if (response.data.success) {
                setData(response.data.data);
            } else {
                setError('Failed to fetch data');
            }
        } catch (err) {
            setError(err.response?.data?.error || 'An error occurred while fetching data');
            console.error('Error fetching data:', err);
        } finally {
            setLoading(false);
        }
    };

    // Filter data based on search term
    const filteredData = data.filter(row => {
        if (!searchTerm) return true;
        return Object.values(row).some(value =>
            value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        );
    });

    // Get visible columns (exclude some sensitive fields)
    const getVisibleColumns = () => {
        if (data.length === 0) return [];
        const excludeColumns = ['password'];
        return Object.keys(data[0]).filter(key => !excludeColumns.includes(key));
    };

    const formatCellValue = (value) => {
        if (value === null || value === undefined) return '-';
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (value instanceof Date || (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/))) {
            try {
                const date = new Date(value);
                return date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                });
            } catch {
                return value;
            }
        }
        return value.toString();
    };

    return (
        <div className="data-display-container">
            <div className="data-display-header">
                <div className="header-content">
                    <h1 className="page-title">
                        <span className="title-icon">📊</span>
                        Database Viewer
                    </h1>
                    <p className="page-subtitle">Explore and view all database tables</p>
                </div>
            </div>

            <div className="controls-section">
                <div className="control-group">
                    <label htmlFor="table-select" className="control-label">
                        Select Table
                    </label>
                    <select
                        id="table-select"
                        className="table-select"
                        value={selectedTable}
                        onChange={(e) => setSelectedTable(e.target.value)}
                    >
                        {tables.map(table => (
                            <option key={table} value={table}>
                                {table}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="control-group">
                    <label htmlFor="search-input" className="control-label">
                        Search Data
                    </label>
                    <input
                        id="search-input"
                        type="text"
                        className="search-input"
                        placeholder="Type to search across all columns..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <button
                    className="refresh-button"
                    onClick={fetchTableData}
                    disabled={loading}
                >
                    <span className="refresh-icon">🔄</span>
                    Refresh
                </button>
            </div>

            {loading && (
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading data from {selectedTable}...</p>
                </div>
            )}

            {error && (
                <div className="error-state">
                    <span className="error-icon">⚠️</span>
                    <p>{error}</p>
                    <button className="retry-button" onClick={fetchTableData}>
                        Try Again
                    </button>
                </div>
            )}

            {!loading && !error && data.length === 0 && (
                <div className="empty-state">
                    <span className="empty-icon">📭</span>
                    <h3>No Data Found</h3>
                    <p>The selected table appears to be empty.</p>
                </div>
            )}

            {!loading && !error && data.length > 0 && (
                <div className="data-section">
                    <div className="data-info">
                        <span className="data-count">
                            Showing <strong>{filteredData.length}</strong> of <strong>{data.length}</strong> records
                        </span>
                    </div>

                    <div className="table-wrapper">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    {getVisibleColumns().map(column => (
                                        <th key={column}>{column}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData.map((row, index) => (
                                    <tr key={index}>
                                        {getVisibleColumns().map(column => (
                                            <td key={column} data-label={column}>
                                                {formatCellValue(row[column])}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {filteredData.length === 0 && searchTerm && (
                        <div className="no-results">
                            <p>No results found for "{searchTerm}"</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default DataDisplay;
